/// <reference lib="dom" />
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { apiClient } from "../../constants.ts";

export type Theme = "light" | "dark" | "system";

export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage?.setItem(key, value);
    } catch {
      // Storage access denied or quota exceeded
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage?.removeItem(key);
    } catch {
      // Storage access denied
    }
  },
};

export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      return typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(key)
        : null;
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(key, value);
      }
    } catch {
      // Storage access denied or quota exceeded
    }
  },
  removeItem: (key: string): void => {
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Storage access denied
    }
  },
};

const getInitialTheme = (): Theme => {
  const stored = safeLocalStorage.getItem("theme");
  return stored === "dark" || stored === "light" || stored === "system"
    ? stored
    : "system";
};

export const themeSignal = signal<Theme>(getInitialTheme());

const updateHtmlClass = () => {
  if (typeof document === "undefined") return;
  const theme = themeSignal.value;
  const isDark = theme === "dark" || (
    theme === "system" &&
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-color-scheme: dark)").matches
  );
  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
};

export const setTheme = (theme: Theme) => {
  themeSignal.value = theme;
  safeLocalStorage.setItem("theme", theme);
  updateHtmlClass();
};

export const toggleTheme = () => {
  const current = themeSignal.value;
  const next = current === "light"
    ? "dark"
    : current === "dark"
    ? "system"
    : "light";
  setTheme(next);
};

export const getActiveTheme = (): "light" | "dark" => {
  const theme = themeSignal.value;
  if (theme === "system") {
    return typeof matchMedia !== "undefined" &&
        matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return theme;
};

if (typeof document !== "undefined") {
  updateHtmlClass();
  if (typeof matchMedia !== "undefined") {
    matchMedia("(prefers-color-scheme: dark)").addEventListener(
      "change",
      () => {
        if (themeSignal.value === "system") {
          updateHtmlClass();
        }
      },
    );
  }
}

export const fireAnalyticsEventLegacy = (
  eventName: string,
  eventData?: Record<string, unknown>,
) => {
  if (typeof globalThis !== "undefined" && "dataLayer" in globalThis) {
    (globalThis as unknown as { dataLayer: unknown[] }).dataLayer?.push({
      event: eventName,
      ...eventData,
    });
  }
};

export const referralStorageKey = "p2b_referral_id";
export const visitorStorageKey = "p2b_visitor_id";
const sessionVisitTrackedKey = "p2b_visit_tracked";

export const getOrInitVisitorId = (): string => {
  const existing = safeLocalStorage.getItem(visitorStorageKey);
  if (existing) return existing;
  const newId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `v_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  safeLocalStorage.setItem(visitorStorageKey, newId);
  return newId;
};

export const trackVisitorTelemetry = () => {
  if (typeof window === "undefined") return;
  const alreadyTracked = Boolean(
    safeSessionStorage.getItem(sessionVisitTrackedKey),
  );
  if (alreadyTracked) return;
  safeSessionStorage.setItem(sessionVisitTrackedKey, "1");

  const visitorId = getOrInitVisitorId();
  const rawRef = safeLocalStorage.getItem(referralStorageKey) ||
    detectReferralSource();
  const attribution = rawRef ? `ref:${rawRef}` : "organic";

  apiClient({
    endpoint: "track-visitor-telemetry",
    payload: {
      visitorId,
      attribution,
    },
  }).catch((e: unknown) =>
    console.error("Failed to track visitor telemetry:", e)
  );
};

const referralVisitTrackedKey = (referral: string) =>
  `${referralStorageKey}-visit-tracked:${referral}`;

const trackReferralVisitOnce = (referral: string) => {
  const key = referralVisitTrackedKey(referral);
  if (safeLocalStorage.getItem(key)) return;
  safeLocalStorage.setItem(key, "1");
  apiClient({ endpoint: "track-referral-visit", payload: { ref: referral } })
    .catch((e: unknown) => console.error("Failed to track referral visit:", e));
};

export const detectReferralSource = (
  search = globalThis.location?.search,
  referrer = globalThis.document?.referrer,
  hostname = globalThis.location?.hostname,
): string | null => {
  const params = new URLSearchParams(search);
  const explicit = params.get("utm_source") ||
    params.get("ref") ||
    params.get("utm_campaign") ||
    params.get("utm_medium");
  if (explicit) return explicit;
  if (params.get("gclid")) return "google_ads";
  if (params.get("rdt_cid")) return "reddit_ads";

  if (!referrer) return null;
  try {
    const refUrl = new URL(referrer);
    const refHost = refUrl.hostname.toLowerCase();
    if (hostname && refHost.includes(hostname.toLowerCase())) return null;
    const isDomain = (d: string) => refHost === d || refHost.endsWith(`.${d}`);
    if (isDomain("reddit.com")) return "reddit";
    if (isDomain("google.com") || refHost.startsWith("google.")) {
      return "google";
    }
    if (isDomain("t.co") || isDomain("twitter.com") || isDomain("x.com")) {
      return "x";
    }
    if (isDomain("news.ycombinator.com")) return "hacker_news";
    if (isDomain("youtube.com")) return "youtube";
    if (isDomain("linkedin.com")) return "linkedin";
    if (isDomain("facebook.com")) return "facebook";
    if (isDomain("instagram.com")) return "instagram";
    if (isDomain("github.com")) return "github";
    return refHost.replace(/^www\./, "");
  } catch {
    return null;
  }
};

export const captureReferralFromUrl = () => {
  if (safeLocalStorage.getItem(referralStorageKey)) return;
  const referral = detectReferralSource();
  if (!referral) return;
  safeLocalStorage.setItem(referralStorageKey, referral);
  trackReferralVisitOnce(referral);
};

// Prerendered pages (paths.ts prerenderedRoutes) are hydrated against HTML
// built without a query string, so reading a query param during the first
// render produces a different tree than the prerendered markup and hydration
// mangles the leftover DOM. Reading it after mount keeps the first render
// identical to the prerendered HTML.
export const useQueryParam = (name: string) => {
  const [value, setValue] = useState("");
  useEffect(() => {
    setValue(new URL(globalThis.location.href).searchParams.get(name) ?? "");
  }, []);
  return value;
};
